import React, {useEffect, useState} from 'react'
import {useIntl} from 'react-intl'
import {Flex, Text, useMultiStyleConfig, StackProps} from '@chakra-ui/react'
import {Input, Icon, Button} from '_uiComponents'
import {useAnalytics, useOpenModal} from '_customHooks'

interface NewsletterInputProps {
    btnLabel?: string
    analyticsLabel: string
    onSubmit?: () => void
    initialMailValue?: string
}

/**
 * @name NewsletterInput
 *
 * @component
 *
 * @description
 * Renders an input field for subscribing to a newsletter along with a button to submit the email.
 *
 * @param {NewsletterInputProps} props - The properties object.
 * @param {string} props.btnLabel - The label for the submit Button.
 * @param {string} props.analyticsLabel - The label for the analytics event to be thrown
 * @param {() => void} props.onSubmit - A function to be called with the submit of the input
 * @param {string} props.initialMailTrigger - The mail sent as input from the trigger of personalization in the small newsletter modal
 *
 * @returns {JSX.Element} Rendered `NewsletterInput` component.
 */
const NewsletterInput: React.FC<NewsletterInputProps> = ({btnLabel, analyticsLabel, onSubmit, initialMailValue}): JSX.Element => {
    const styles = useMultiStyleConfig('NewsletterInput') as Record<string, StackProps>
    const {formatMessage} = useIntl()
    const [newsletterValue, setNewsletterValue] = useState('')
    const {analyticsEventNewsletter} = useAnalytics()
    const {openNewsletterModal} = useOpenModal()

    const subscribeLabel = btnLabel ?? formatMessage({id: 'label.newsletter.form.subscribe', defaultMessage: 'Subscribe'})
    const inputPlaceholder = formatMessage({id: 'label.input.login.email', defaultMessage: 'Enter e-mail'})

    const triggerNewsletterModal = () => {
        analyticsEventNewsletter({action: 'start', label: analyticsLabel})

        onSubmit?.()

        openNewsletterModal({
            newsletterValue,
            analyticsLabel: analyticsLabel,
            analyticsEventNewsletter: () => analyticsEventNewsletter({action: 'dismiss', label: analyticsLabel}),
        })
    }

    useEffect(() => {
        if (initialMailValue) {
            setNewsletterValue(initialMailValue)
        }
    }, [initialMailValue])

    return (
        <Flex {...styles.wrapper} className="newsletterInput">
            <Text as="label" htmlFor="newsletter-email" srOnly>
                {formatMessage({id: 'label.newsletter', defaultMessage: 'Newsletter'})}
            </Text>

            <Input
                type="email"
                name="newsletter"
                placeholder={inputPlaceholder}
                value={newsletterValue}
                onChange={(e) => {
                    setNewsletterValue(e.target.value)
                }}
                aria-label={formatMessage({id: 'label.newsletter', defaultMessage: 'Newsletter'})}
                data-intent-redact
            />

            <Button variant="newsletter" type="button" size="newsletter" onClick={triggerNewsletterModal} data-testid="newsletter-submit">
                <Icon name="IconEmailSmall" size="sm" />
                <Text>{subscribeLabel}</Text>
            </Button>
        </Flex>
    )
}

export {NewsletterInput}
